import { FC, KeyboardEvent, useEffect } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import PerIcon from '../../../components/custom_components/Icon'
import PerText from '../../../components/custom_components/Text'
import { useCompanyManagerDashboardBumOfCompany } from '../../../graphql/company-manager-dashboard/hooks/useCompanyManagerDashboardBusinessUnitsAndBranchOfficesQueries'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { BumOfBusinessUnitDto, BusinessUnitTiny, EmployeeWithLevelDto } from '../../../types'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useAuth } from '../../../context/auth-context'
import { PerAvatarDashboardMonitoringTable } from '../../../components/custom_components/AvatarPlus/PerAvatarDashboardMonitoringTable'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { useDashboardStore } from '../DashboardStore'

type DashboardDistributionsBUMTabContentProps = {
  selectedCompanyId: string
}

const DashboardDistributionsBUMTabContent: FC<DashboardDistributionsBUMTabContentProps> = ({ selectedCompanyId }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const [appraisalMonitoringFilters, setAppraisalMonitoringFilters, selectedDashboardRoles] = useDashboardStore((state) => [
    state.appraisalMonitoringFilters,
    state.setAppraisalMonitoringFilters,
    state.selectedDashboardRoles,
  ])

  const {
    data: bumOfCompanyData,
    loading: loadingBumOfCompany,
    error: bumOfCompanyError,
  } = useCompanyManagerDashboardBumOfCompany(selectedCompanyId, selectedDashboardRoles)

  useEffect(() => {
    if (!bumOfCompanyError) {
      return
    }
    showNotification({
      type: 'error',
      message: t('dashboard.companyManager.filtersArea.distributions.bumOfCompanyQueryError.title'),
      description: t('dashboard.companyManager.filtersArea.distributions.bumOfCompanyQueryError.description'),
    })
  }, [bumOfCompanyError, t])

  const bumList: BumOfBusinessUnitDto[] = bumOfCompanyData?.companyManagerDashboardBumOfCompany.buManagers ?? []

  const flatBumManagers = bumList.flatMap(({ businessUnit, buManagers }) => buManagers.map((buManager) => ({ buManager, businessUnit })))

  const allBumManagers = Object.values(
    flatBumManagers.reduce<Record<string, { buManager: EmployeeWithLevelDto; businessUnits: BusinessUnitTiny[] }>>((acc, row) => {
      const managerKey = String(row.buManager.id)
      const current = acc[managerKey]

      if (!current) {
        acc[managerKey] = { buManager: row.buManager, businessUnits: [row.businessUnit] }
        return acc
      }

      const alreadyPresent = current.businessUnits.some((bu) => String(bu.id) === String(row.businessUnit.id))
      if (!alreadyPresent) {
        current.businessUnits.push(row.businessUnit)
      }
      return acc
    }, {}),
  )
    .map((entry) => ({
      ...entry,
      businessUnits: [...entry.businessUnits].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    }))
    .sort((a, b) => {
      const nameA = computeFullname(user.nameDisplayPreference, a.buManager.firstName, a.buManager.lastName)
      const nameB = computeFullname(user.nameDisplayPreference, b.buManager.firstName, b.buManager.lastName)
      return nameA.localeCompare(nameB)
    })
  const bumFilterIdKey: string | undefined = appraisalMonitoringFilters.selectedBuManager?.id

  const onBuManagerRowClick = (buManager: EmployeeWithLevelDto) => {
    const turningOff = bumFilterIdKey === buManager.id
    setAppraisalMonitoringFilters({
      ...appraisalMonitoringFilters,
      selectedBusinessUnit: turningOff ? appraisalMonitoringFilters.selectedBusinessUnit : undefined,
      selectedBuManager: turningOff ? undefined : buManager,
    })
  }

  const onBuManagerRowKeyDown = (e: KeyboardEvent, buManager: EmployeeWithLevelDto) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onBuManagerRowClick(buManager)
    }
  }

  return (
    <div className="dashboard-filters-area__distributions__bum-content">
      {loadingBumOfCompany || !!bumOfCompanyError ? (
        <PerFlexRow justify="center" align="middle" className="fullHeight">
          <NewLoadingAndErrors
            t={t}
            isLoading={loadingBumOfCompany}
            hasNoData={false}
            showSpinner
            error={bumOfCompanyError}
            iconSize={100}
            showOnlyIcon
          />
        </PerFlexRow>
      ) : (
        <>
          <div className="dashboard-filters-area__distributions__bum-managers-list">
            {allBumManagers.map(({ buManager, businessUnits }) => {
              const isSelected = !!bumFilterIdKey && buManager.id === bumFilterIdKey
              return (
                <div
                  key={buManager.id}
                  className={classNames('dashboard-filters-area__distributions__bum-manager-row', {
                    'dashboard-filters-area__distributions__bum-manager-row--selected': isSelected,
                  })}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={() => onBuManagerRowClick(buManager)}
                  onKeyDown={(e) => onBuManagerRowKeyDown(e, buManager)}
                >
                  <PerAvatarDashboardMonitoringTable
                    nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                    employeeId={buManager.id}
                    firstName={buManager.firstName}
                    lastName={buManager.lastName}
                    subtitle={t(`employee.contract.level.${buManager.currentLevel}`)}
                    whiteTextColor={isSelected}
                  />
                  <div className="dashboard-filters-area__distributions__bum-manager-row-end">
                    <div className="dashboard-filters-area__distributions__bum-bu-list">
                      {businessUnits.map((bu) => (
                        <PerText key={bu.id} color={isSelected ? 'white' : 'secondary'} fontSize="xs">
                          {bu.displayName}
                        </PerText>
                      ))}
                    </div>
                    <span className="dashboard-filters-area__distributions__bum-close" aria-hidden>
                      <PerIcon name="close_stroke" color="white" size={10} variant="decoration" />
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default DashboardDistributionsBUMTabContent
